/*
 * signalr-client.js — browser glue for PIRO WebPOS/CP SignalR notifications.
 *
 * One SignalR connection per browser: the tab holding the Web Lock master lock connects and
 * re-broadcasts every notification to the other tabs over a BroadcastChannel. Every tab (master
 * included) runs the same decision logic from signalr-core.js to decide whether it acts on a
 * notification. Without Web Locks (no secure context / old browser) every tab opens its own
 * connection ("pertab" mode).
 *
 * Framework-agnostic: no Vue/Nuxt imports. The @microsoft/signalr connection comes in through
 * options.buildConnection so this file can be reused by the Customer Portal and unit tested with a
 * fake connection. Ported from Fusion (PIRO.Fusion.v1/Resources/JScripts/signalr-client.js).
 * Rules: PIRO.API.v3/Docs/signalR/developer-guide.md.
 */
((root, factory) => {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('./signalr-core'));
  } else {
    root.PIROSignalRClient = factory(root.PIROSignalRCore);
  }
})(typeof self !== 'undefined' ? self : this, (core) => {
  'use strict';

  const MARKER_PREFIX = 'piro.webpos.signalr.done.'; // sweep matches every namespace
  const FORCE_MODE_KEY = 'piro.webpos.signalr.forceMode';
  const DEBUG_KEY = 'piro.webpos.signalr.debug';
  const MARKER_SWEEP_MS = 5 * 60 * 1000;
  const CLAIM_HOLD_MS = 25;      // keep the claim lock a moment so other renderers see the marker
  const RECONNECT_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000];

  const STATES = {
    connecting: 'connecting',
    connected: 'connected',
    reconnecting: 'reconnecting',
    disconnected: 'disconnected'
  };

  /** Short non-cryptographic hash (FNV-1a, hex) for namespacing shared browser resources. */
  function hashString(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return ('0000000' + h.toString(16)).slice(-8);
  }

  function delay(ms) {
    return new Promise(resolve => { setTimeout(resolve, ms); });
  }

  /**
   * @param {object} options
   *   buildConnection(): HubConnection   - @microsoft/signalr connection with URL + accessTokenFactory
   *   namespaceSeed: string              - identity of this instance+user (e.g. origin + '|' + userId).
   *                                        Web Locks/BroadcastChannel/localStorage are per ORIGIN; the
   *                                        seed keeps sessions of different users (or a shared-origin
   *                                        hosting) from coordinating with each other.
   *   settings?: object                  - timing overrides (core settings + pingIntervalMs,
   *                                        pingTimeoutMs, reconnectGiveUpMs, markerTtlMs)
   *   onStateChanged?: (state, meta) => void - 'connecting' | 'connected' | 'reconnecting' | 'disconnected'
   * @returns client instance; call destroy() on logout, create a new instance on the next login.
   */
  function createSignalRClient(options) {
    const settings = options.settings || {};
    const NAMESPACE = hashString(String(options.namespaceSeed || ''));
    const CHANNEL_NAME = 'piro.webpos.signalr.notifications.v1.' + NAMESPACE;
    const MASTER_LOCK = 'piro.webpos.signalr.master.v1.' + NAMESPACE;

    const state = {
      started: false,
      destroyed: false,
      mode: null,                 // 'locks' | 'pertab'
      tabId: 'tab-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36),
      isMaster: false,
      wantConnection: false,
      connection: null,
      connectionState: STATES.disconnected,
      connectAttempt: 0,
      reconnectTimer: null,
      disconnectedSince: null,
      giveUpTimer: null,
      lockRequest: null,          // the pending or granted Web Lock request of this tab
      lockFailures: 0,
      pingTimer: null,
      pingTimeout: null,
      sweepTimer: null,
      bus: null,
      domUnbinders: []
    };

    const debug = safeGet(localStorage, DEBUG_KEY) === '1';

    // ------------------------------------------------------------ utilities

    function safeGet(storage, key) {
      try { return storage.getItem(key); } catch (_) { return null; }
    }

    function safeSet(storage, key, value) {
      try { storage.setItem(key, value); return true; } catch (_) { return false; }
    }

    function safeRemove(storage, key) {
      try { storage.removeItem(key); } catch (_) { /* ignore */ }
    }

    function log(level, message, data) {
      if (level === 'debug' && !debug) return;
      const fn = level === 'error' ? console.error : (level === 'warn' ? console.warn : console.log);
      try {
        if (data === undefined) fn.call(console, '[signalr] ' + message);
        else fn.call(console, '[signalr] ' + message, data);
      } catch (_) { /* ignore */ }
    }

    function now() { return Date.now(); }

    function setConnectionState(value, meta) {
      if (state.connectionState === value) return;
      state.connectionState = value;
      if (typeof options.onStateChanged === 'function') {
        try { options.onStateChanged(value, meta || {}); } catch (err) { log('error', 'onStateChanged threw', err); }
      }
    }

    // ------------------------------------------------------------ handled markers (shared via localStorage)

    function markerTtl() {
      return typeof settings.markerTtlMs === 'number' && settings.markerTtlMs > 0 ? settings.markerTtlMs : 60000;
    }

    /** Storage key of the cross-tab "handled" marker for a core marker key. */
    function markerStorageKey(coreKey) {
      return MARKER_PREFIX + NAMESPACE + '.' + String(coreKey);
    }

    function readMarker(key) {
      const raw = safeGet(localStorage, key);
      if (!raw) return null;
      try {
        const m = JSON.parse(raw);
        if (!m || typeof m.ts !== 'number' || now() - m.ts > markerTtl()) {
          safeRemove(localStorage, key);
          return null;
        }
        return m;
      } catch (_) {
        safeRemove(localStorage, key);
        return null;
      }
    }

    function writeMarker(key) {
      return safeSet(localStorage, key, JSON.stringify({ tab: state.tabId, ts: now() }));
    }

    function sweepMarkers() {
      try {
        const stale = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.indexOf(MARKER_PREFIX) === 0 && !readMarker(k)) stale.push(k);
        }
        stale.forEach(k => safeRemove(localStorage, k));
      } catch (_) { /* ignore */ }
    }

    // ------------------------------------------------------------ claims

    function hasWebLocks() {
      return typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function';
    }

    function claimWithLock(coreKey) {
      const key = markerStorageKey(coreKey);
      return navigator.locks.request(key, { ifAvailable: true }, lock => {
        if (!lock) return false;
        if (readMarker(key)) return false;
        // Holding the lock with no marker present makes this tab the unique claimant. A failed marker
        // write (storage quota / privacy mode) may cost a duplicate in another tab later, never a lost
        // notification.
        if (!writeMarker(key)) log('warn', 'could not write the handled marker for ' + key);
        // Keep the lock a moment: localStorage writes reach other renderer processes asynchronously, so
        // a tab granted the lock right after ours could otherwise read a stale, marker-less view.
        return delay(CLAIM_HOLD_MS).then(() => true);
      }).catch(err => {
        log('warn', 'lock claim failed, falling back to storage claim', err);
        return claimWithStorage(coreKey);
      });
    }

    function claimWithStorage(coreKey) {
      const key = markerStorageKey(coreKey);
      // Compare-and-set on localStorage; the short verify delay resolves rare simultaneous writes.
      if (readMarker(key)) return Promise.resolve(false);
      if (!writeMarker(key)) return Promise.resolve(true); // storage unavailable: better a duplicate than a loss
      return delay(CLAIM_HOLD_MS).then(() => {
        const m = readMarker(key);
        return !m || m.tab === state.tabId;
      });
    }

    // ------------------------------------------------------------ core instance

    const env = {
      isVisible: () => document.visibilityState === 'visible',
      hasFocus: () => {
        try { return document.hasFocus(); } catch (_) { return false; }
      },
      isMaster: () => state.isMaster,
      delay,
      isClaimed: coreKey => !!readMarker(markerStorageKey(coreKey)),
      tryClaim: coreKey => (state.mode === 'locks' ? claimWithLock(coreKey) : claimWithStorage(coreKey)),
      log
    };

    const coreInstance = core.createCore(env, settings);

    // ------------------------------------------------------------ inter-tab bus

    function createBus() {
      const handlers = [];
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          const bc = new BroadcastChannel(CHANNEL_NAME);
          bc.onmessage = ev => handlers.forEach(h => h(ev.data));
          return {
            post(msg) {
              try { bc.postMessage(msg); } catch (err) { log('warn', 'bus post failed', err); }
            },
            on(fn) { handlers.push(fn); },
            close() { try { bc.close(); } catch (_) { /* ignore */ } }
          };
        } catch (_) { /* fall through */ }
      }
      // localStorage fallback (very old browsers)
      const LS_KEY = CHANNEL_NAME + '.msg';
      const onStorage = e => {
        if (e.key !== LS_KEY || !e.newValue) return;
        try {
          const envelope = JSON.parse(e.newValue);
          if (envelope && envelope.origin !== state.tabId) handlers.forEach(h => h(envelope.data));
        } catch (_) { /* ignore */ }
      };
      window.addEventListener('storage', onStorage);
      return {
        post(msg) {
          safeSet(localStorage, LS_KEY, JSON.stringify({ origin: state.tabId, ts: now(), data: msg }));
          safeRemove(localStorage, LS_KEY);
        },
        on(fn) { handlers.push(fn); },
        close() { window.removeEventListener('storage', onStorage); }
      };
    }

    function onBusMessage(msg) {
      if (!msg || !msg.type || state.destroyed) return;
      switch (msg.type) {
        case 'notification':
          onPong(); // only a live master relays notifications
          if (state.mode === 'locks') {
            if (!state.isMaster) setConnectionState(STATES.connected, { via: 'relay' });
            coreInstance.process(msg.payload);
          }
          break;
        case 'ping':
          if (state.isMaster) state.bus.post({ type: 'pong', from: state.tabId, connected: isConnected() });
          break;
        case 'pong':
          onPong();
          if (!state.isMaster) setConnectionState(msg.connected ? STATES.connected : STATES.reconnecting, { via: 'pong' });
          break;
        case 'status':
          onPong(); // any status broadcast proves a live master
          if (!state.isMaster) {
            if (msg.state === 'connected' || msg.state === 'reconnected') setConnectionState(STATES.connected, { via: 'status' });
            else if (msg.state === 'disconnected') setConnectionState(STATES.reconnecting, { via: 'status' });
          }
          log('debug', 'master status: ' + msg.state);
          break;
        default:
          break;
      }
    }

    // ------------------------------------------------------------ SignalR connection (@microsoft/signalr)

    function getConnection() {
      if (state.connection) return state.connection;
      const connection = options.buildConnection();

      connection.on('notify', onServerNotification);

      // No withAutomaticReconnect: reconnects are scheduled here (infinite, backoff + jitter) so the
      // master give-up/hand-over logic sees every transition.
      connection.onclose(err => {
        if (state.destroyed || !state.wantConnection) return;
        if (state.disconnectedSince == null) state.disconnectedSince = now();
        setConnectionState(STATES.reconnecting, { error: err });
        state.bus.post({ type: 'status', state: 'disconnected' });
        log('warn', 'connection closed, reconnecting', err);
        scheduleReconnect();
        armGiveUp();
      });

      state.connection = connection;
      return connection;
    }

    function isConnected() {
      return !!state.connection && state.connection.state === 'Connected';
    }

    function startConnection() {
      if (state.destroyed || !state.wantConnection) return;
      const connection = getConnection();
      if (connection.state !== 'Disconnected') return; // starting or already live

      setConnectionState(state.connectAttempt > 0 ? STATES.reconnecting : STATES.connecting);
      log('debug', 'starting connection (' + state.mode + ')');
      connection.start()
        .then(() => {
          if (state.destroyed || !state.wantConnection) {
            // stop() lost the race with a start already in flight: close the connection it could not.
            connection.stop().catch(() => {});
            return;
          }
          state.connectAttempt = 0;
          state.disconnectedSince = null;
          clearGiveUp();
          setConnectionState(STATES.connected);
          state.bus.post({ type: 'status', state: 'connected' });
          log('debug', 'connected as ' + (state.isMaster ? 'master' : state.mode));
        })
        .catch(err => {
          if (state.destroyed || !state.wantConnection) return;
          log('warn', 'start failed', err);
          if (state.disconnectedSince == null) state.disconnectedSince = now();
          setConnectionState(STATES.reconnecting, { error: err });
          scheduleReconnect();
          armGiveUp();
        });
    }

    function stopConnection() {
      clearTimeout(state.reconnectTimer);
      state.reconnectTimer = null;
      clearGiveUp();
      if (!state.connection) return;
      try {
        state.connection.stop().catch(err => log('warn', 'stop failed', err));
      } catch (err) {
        log('warn', 'stop failed', err);
      }
    }

    function scheduleReconnect() {
      if (state.destroyed || !state.wantConnection || state.reconnectTimer) return;
      const base = RECONNECT_BACKOFF_MS[Math.min(state.connectAttempt, RECONNECT_BACKOFF_MS.length - 1)];
      const wait = Math.round(base * (0.8 + Math.random() * 0.4)); // +/-20% jitter against thundering herds
      state.connectAttempt++;
      state.reconnectTimer = setTimeout(() => {
        state.reconnectTimer = null;
        if (!state.wantConnection || isConnected()) return;
        startConnection();
      }, wait);
    }

    function armGiveUp() {
      if (state.mode !== 'locks' || !state.isMaster || state.giveUpTimer) return;
      const limit = typeof settings.reconnectGiveUpMs === 'number' ? settings.reconnectGiveUpMs : 15000;
      state.giveUpTimer = setTimeout(() => {
        state.giveUpTimer = null;
        if (!state.isMaster || isConnected()) return;
        log('warn', 'still disconnected after ' + limit + 'ms, handing the master lock to another tab');
        releaseMasterLock();
      }, limit);
    }

    function clearGiveUp() {
      clearTimeout(state.giveUpTimer);
      state.giveUpTimer = null;
    }

    function onServerNotification(notification) {
      log('debug', 'received ' + (notification && notification.notificationType), notification);
      if (state.mode === 'locks') {
        state.bus.post({ type: 'notification', payload: notification });
      }
      coreInstance.process(notification);
    }

    // ------------------------------------------------------------ master election (Web Locks)

    /**
     * Queue for (or steal) the master lock. Each call owns a request record so that the asynchronous
     * settlement of an older request (abort, steal) can never be confused with the current one.
     * Web Locks semantics relied upon: the callback runs while the lock is held and the lock is
     * released when the returned promise settles; `steal: true` releases the current holder (its
     * request() rejects with AbortError) but does not reject other queued requests; aborting our own
     * pending request rejects it.
     */
    function requestMasterLock(opts) {
      if (state.destroyed || state.mode !== 'locks' || !hasWebLocks()) return;
      if (state.lockRequest && !state.lockRequest.settled) return; // one outstanding request at a time

      const steal = !!(opts && opts.steal);
      const req = { controller: new AbortController(), granted: false, settled: false, selfAborted: false, release: null, steal };
      state.lockRequest = req;
      // The spec rejects `signal` combined with `steal` (NotSupportedError): a steal request carries no
      // signal; only queued requests need to be abortable.
      const lockOpts = steal ? { steal: true } : { signal: req.controller.signal };

      let promise;
      try {
        promise = navigator.locks.request(MASTER_LOCK, lockOpts, () => {
          // Granted after this tab was destroyed (logout/unload raced the queue): give the lock
          // back at once - holding it would block every other tab's election forever.
          if (state.destroyed) return Promise.resolve();
          req.granted = true;
          state.lockFailures = 0;
          // The release handle must exist BEFORE becomeMaster(), whose failure paths may release
          // synchronously; a release requested before the promise existed would be lost.
          const released = new Promise(resolve => { req.release = resolve; });
          becomeMaster();
          return released;
        });
      } catch (err) {
        promise = Promise.reject(err);
      }

      promise.then(() => {
        req.settled = true;
        // We released the lock ourselves (reconnect give-up or destroy). Queued tabs go first, then queue again.
        if (state.lockRequest === req) loseMaster('released');
        if (!state.destroyed) setTimeout(() => requestMasterLock(), 2000);
      }).catch(err => {
        req.settled = true;
        if (req.selfAborted) return; // cancelled on purpose right before a steal
        if (req.granted) {
          if (state.lockRequest === req) loseMaster('stolen');
          log('warn', 'master lock taken by another tab', err && err.name);
          if (!state.destroyed) setTimeout(() => requestMasterLock(), 1000);
          return;
        }
        // Never granted and not our abort: the Lock API itself failed (privacy mode, policy...).
        state.lockFailures++;
        log('warn', 'master lock request failed (' + state.lockFailures + ')', err && (err.name + ': ' + err.message));
        if (state.lockFailures >= 3) {
          switchToPerTab('Web Locks failing');
        } else if (!state.destroyed) {
          setTimeout(() => requestMasterLock(), 2000);
        }
      });
    }

    function becomeMaster() {
      if (state.isMaster || state.destroyed) return;
      state.isMaster = true;
      state.wantConnection = true;
      stopPinging();
      log('debug', 'this tab is master');
      state.bus.post({ type: 'status', state: 'master', from: state.tabId });
      startConnection();
    }

    function loseMaster(reason) {
      const was = state.isMaster;
      state.isMaster = false;
      if (state.mode === 'locks') {
        state.wantConnection = false;
        stopConnection();
      }
      if (was) log('debug', 'master lost: ' + reason);
      if (!state.destroyed) startPinging();
    }

    function releaseMasterLock() {
      const req = state.lockRequest;
      if (req && req.granted && typeof req.release === 'function') {
        const resolve = req.release;
        req.release = null;
        resolve();
      }
    }

    function stealMasterLock() {
      if (state.isMaster || state.destroyed || state.mode !== 'locks') return;
      log('warn', 'master unresponsive, stealing the lock');
      const pending = state.lockRequest;
      if (pending && !pending.settled && !pending.granted) {
        pending.selfAborted = true;
        pending.settled = true; // allow the steal request to be created right away
        try { pending.controller.abort(); } catch (_) { /* ignore */ }
      }
      requestMasterLock({ steal: true });
    }

    function switchToPerTab(reason) {
      if (state.mode === 'pertab' || state.destroyed) return;
      log('warn', 'switching to per-tab connection mode: ' + reason);
      stopPinging();
      state.mode = 'pertab';
      state.isMaster = false;
      state.wantConnection = true;
      startConnection();
    }

    // ------------------------------------------------------------ master health probe (slaves)

    function startPinging() {
      if (state.destroyed || state.mode !== 'locks' || state.isMaster || state.pingTimer) return;
      const interval = typeof settings.pingIntervalMs === 'number' && settings.pingIntervalMs > 0 ? settings.pingIntervalMs : 30000;
      state.pingTimer = setInterval(() => {
        if (document.visibilityState === 'visible') ping();
      }, interval);
      // Probe right away (not only after a full interval): a fresh slave learns the master's
      // connection state for the nav-bar indicator, and a dead master is stolen quickly.
      setTimeout(() => { if (!state.destroyed && !state.isMaster) ping(); }, 500);
    }

    function stopPinging() {
      clearInterval(state.pingTimer);
      state.pingTimer = null;
      clearTimeout(state.pingTimeout);
      state.pingTimeout = null;
    }

    function ping() {
      if (state.isMaster || state.pingTimeout || state.mode !== 'locks' || state.destroyed) return;
      const timeout = typeof settings.pingTimeoutMs === 'number' && settings.pingTimeoutMs > 0 ? settings.pingTimeoutMs : 3000;
      state.bus.post({ type: 'ping', from: state.tabId });
      state.pingTimeout = setTimeout(() => {
        state.pingTimeout = null;
        stealMasterLock();
      }, timeout);
    }

    function onPong() {
      clearTimeout(state.pingTimeout);
      state.pingTimeout = null;
    }

    // ------------------------------------------------------------ lifecycle

    function decideMode() {
      const forced = safeGet(localStorage, FORCE_MODE_KEY);
      if (forced === 'pertab') return 'pertab';
      if (forced === 'locks' && hasWebLocks()) return 'locks';
      return hasWebLocks() ? 'locks' : 'pertab';
    }

    function bindDom(target, event, handler) {
      target.addEventListener(event, handler);
      state.domUnbinders.push(() => target.removeEventListener(event, handler));
    }

    function start() {
      if (state.started || state.destroyed) return;
      state.started = true;

      state.mode = decideMode();
      state.bus = createBus();
      state.bus.on(onBusMessage);
      sweepMarkers();
      state.sweepTimer = setInterval(sweepMarkers, MARKER_SWEEP_MS);

      if (state.mode === 'locks') {
        requestMasterLock();
        startPinging();
      } else {
        log('warn', 'Web Locks unavailable (needs HTTPS or a 2022+ browser); per-tab connection mode');
        state.wantConnection = true;
        startConnection();
      }

      // A tab waking from sleep/background must not wait out timers throttled by the browser.
      bindDom(document, 'visibilitychange', () => {
        if (document.visibilityState !== 'visible' || state.destroyed) return;
        if ((state.isMaster || state.mode === 'pertab') && state.wantConnection && !isConnected()) {
          clearTimeout(state.reconnectTimer);
          state.reconnectTimer = null;
          startConnection();
        } else if (state.mode === 'locks' && !state.isMaster) {
          ping();
        }
      });

      // pagehide only: beforeunload can be cancelled by a "Leave site?" prompt, which must not
      // disable this tab. Back/forward cache restore re-joins in pageshow.
      bindDom(window, 'pagehide', onUnload);
      bindDom(window, 'pageshow', ev => { if (ev.persisted) onRestoreFromCache(); });
    }

    function onUnload() {
      if (state.destroyed) return;
      state.destroyed = true;
      stopPinging();
      if (state.wantConnection || isConnected()) {
        // Tell the server right away so parked notifications go to the next master instead of a
        // dead connection.
        stopConnection();
      }
      state.wantConnection = false;
      releaseMasterLock();
    }

    // Back/forward cache restore: the page was hidden with onUnload, but lives on. Re-join.
    function onRestoreFromCache() {
      if (!state.destroyed) return;
      state.destroyed = false;
      state.isMaster = false;
      if (state.mode === 'locks') {
        requestMasterLock();
        startPinging();
      } else {
        state.wantConnection = true;
        startConnection();
      }
    }

    /** Full teardown (logout). The instance cannot be restarted; create a new one on the next login. */
    function destroy() {
      if (state.destroyed && !state.started) return;
      state.destroyed = true;
      state.wantConnection = false;
      stopPinging();
      stopConnection();
      releaseMasterLock();
      // A still-queued (not yet granted) lock request must be withdrawn too, or this dead tab
      // would eventually be granted the master lock.
      const pending = state.lockRequest;
      if (pending && !pending.settled && !pending.granted) {
        pending.selfAborted = true;
        try { pending.controller.abort(); } catch (_) { /* ignore */ }
      }
      state.domUnbinders.forEach(unbind => { try { unbind(); } catch (_) { /* ignore */ } });
      state.domUnbinders = [];
      clearInterval(state.sweepTimer);
      state.sweepTimer = null;
      if (state.bus) { state.bus.close(); state.bus = null; }
      setConnectionState(STATES.disconnected, { via: 'destroy' });
    }

    // ------------------------------------------------------------ public API

    return {
      STATES,
      OUTCOMES: core.OUTCOMES,
      start,
      destroy,
      subscribe: subscriber => coreInstance.subscribe(subscriber),
      setFallbackHandler: fn => coreInstance.setFallbackHandler(fn),
      onOutcome: fn => coreInstance.onOutcome(fn),
      state() {
        return {
          mode: state.mode,
          isMaster: state.isMaster,
          connected: isConnected(),
          connectionState: state.connectionState,
          tabId: state.tabId,
          subscribers: coreInstance.subscriberCount(),
          settings: coreInstance.settings
        };
      }
    };
  }

  return {
    STATES,
    hashString,
    createSignalRClient
  };
});
